import type {
  PaginatedPassengerAddressList,
  PayerOrganization,
} from '@bambi/api-client/schemas';
import type { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';
import { useCallback } from 'react';

import addDefaultTripAddressOption from './addDefaultTripAddressOption';
import getRecentAddressesAsOptions from './getRecentAddressesAsOptions';

type AdditionalResultsQueryFn = (
  query: string,
  options: SelectOption[]
) => Promise<SelectOption[]>;

export const useAdditionalResultsQuery = (
  recentAddressResponse: PaginatedPassengerAddressList | undefined,
  selectedPayer: PayerOrganization | null | undefined
): AdditionalResultsQueryFn => {
  const additionalResultsQueryFn = useCallback(
    async (query: string, options: SelectOption[]) => {
      const additionalOptions = getAdditionalOptions(
        recentAddressResponse,
        selectedPayer,
        query
      );

      // Filter out options that are already in the recent addresses
      const filteredOptions = options.filter((option) => {
        const recentAddress = recentAddressResponse?.results.find(
          (result) => result.address.location?.place_id === option.value
        );
        return !recentAddress;
      });

      return Promise.resolve([...additionalOptions, ...filteredOptions]);
    },
    [recentAddressResponse, selectedPayer]
  );

  return additionalResultsQueryFn;
};

const getAdditionalOptions = (
  recentAddressResponse: PaginatedPassengerAddressList | undefined,
  selectedPayer: PayerOrganization | null | undefined,
  query: string
) => {
  let additionalOptions: SelectOption[] = getRecentAddressesAsOptions(
    recentAddressResponse
  );

  additionalOptions = addDefaultTripAddressOption(
    additionalOptions,
    selectedPayer
  );

  const filteredAdditionalOptions = additionalOptions.filter((option) => {
    return option.label.toLowerCase().includes(query.toLowerCase());
  });

  return filteredAdditionalOptions;
};
